import { View, Text, TouchableOpacity, StyleSheet, Image, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useState } from 'react';
import { ProfilePictureManager } from '@/lib/profile/ProfilePictureManager';
import { Spacing, BorderRadius } from '@/constants/spacing';

interface ProfilePhotoEditorProps {
  profilePictureUri: string | null;
  accentColor: string;
  textColor: string;
  backgroundColor: string;
  onPhotoChanged: () => void;
}

/**
 * ProfilePhotoEditor Component
 * Allows users to upload, change, or remove their profile photo.
 * Displays a large avatar with action buttons.
 */
export function ProfilePhotoEditor({
  profilePictureUri,
  accentColor,
  textColor,
  backgroundColor,
  onPhotoChanged,
}: ProfilePhotoEditorProps) {
  const [isUploading, setIsUploading] = useState(false);

  const handleUploadPhoto = async () => {
    try {
      setIsUploading(true);
      const manager = ProfilePictureManager.getInstance();
      const uri = await manager.pickImage();

      if (uri) {
        onPhotoChanged();
      }
    } catch (error) {
      console.error('Failed to upload photo:', error);
      Alert.alert('Error', 'Failed to upload photo. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemovePhoto = () => {
    Alert.alert(
      'Remove Photo',
      'Are you sure you want to remove your profile photo?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            try {
              const manager = ProfilePictureManager.getInstance();
              await manager.removeProfilePicture();
              onPhotoChanged();
            } catch (error) {
              console.error('Failed to remove photo:', error);
              Alert.alert('Error', 'Failed to remove photo. Please try again.');
            }
          },
        },
      ]
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.avatarContainer}>
        {profilePictureUri ? (
          <Image source={{ uri: profilePictureUri }} style={styles.avatar} />
        ) : (
          <View style={[styles.avatarPlaceholder, { backgroundColor: accentColor }]}>
            <Ionicons name="person" size={60} color="#fff" />
          </View>
        )}
      </View>

      <View style={styles.buttonsContainer}>
        <TouchableOpacity
          style={[styles.button, { backgroundColor }]}
          onPress={handleUploadPhoto}
          disabled={isUploading}
          activeOpacity={0.7}>
          <Ionicons
            name={profilePictureUri ? 'camera-outline' : 'cloud-upload-outline'}
            size={20}
            color={accentColor}
            style={styles.buttonIcon}
          />
          <Text style={[styles.buttonText, { color: textColor }]}>
            {isUploading
              ? 'Uploading...'
              : profilePictureUri
              ? 'Change Photo'
              : 'Upload Photo'}
          </Text>
        </TouchableOpacity>

        {profilePictureUri && (
          <TouchableOpacity
            style={[styles.button, styles.removeButton, { backgroundColor }]}
            onPress={handleRemovePhoto}
            activeOpacity={0.7}>
            <Ionicons
              name="trash-outline"
              size={20}
              color="#ff3b30"
              style={styles.buttonIcon}
            />
            <Text style={[styles.buttonText, styles.removeButtonText]}>Remove Photo</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    paddingVertical: Spacing.xxl,
  },
  avatarContainer: {
    marginBottom: Spacing.xl,
  },
  avatar: {
    width: 120,
    height: 120,
    borderRadius: 60, // circular
  },
  avatarPlaceholder: {
    width: 120,
    height: 120,
    borderRadius: 60, // circular
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonsContainer: {
    flexDirection: 'row',
    gap: Spacing.md,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: Spacing.sm, // normalized from 10
    paddingHorizontal: Spacing.lg,
    borderRadius: BorderRadius.md,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  buttonIcon: {
    marginRight: Spacing.sm,
  },
  buttonText: {
    fontSize: 15,
    fontWeight: '600',
  },
  removeButton: {
    borderColor: '#ff3b3033',
  },
  removeButtonText: {
    color: '#ff3b30',
  },
});
